import React, { useState, useEffect } from 'react';
import { collection, doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { Users, BookOpen, Wrench, Save, Plus, Trash2, ChevronUp, ChevronDown, Edit2, Check, Download, Trophy } from 'lucide-react';
import { lessonsData } from './Lesson';

function AdminDashboard({ onAddHearts, onRemoveHearts, onShowStreak, onShowSummary, onShowNoHearts, onShowLootbox, onShowInstallPWA, onShowInstallPWAIOS, onShowTheory, onShowAchievement, onShowCertificateModal, onDownloadCertificate, onShowCourseDiscovery }) {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Lesson Editor States
  const [selectedCourse, setSelectedCourse] = useState('Python');
  const [selectedStage, setSelectedStage] = useState(1);
  const [selectedSection, setSelectedSection] = useState(1);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [modules, setModules] = useState([]);
  const [loadingLesson, setLoadingLesson] = useState(false);
  const [editingModuleIndex, setEditingModuleIndex] = useState(null);

  const structure = {
    'Python': {
      1: [
        { id: 1, name: "Abschnitt 1: Einführung", range: [1, 10] },
        { id: 2, name: "Abschnitt 2: Basics", range: [11, 20] },
        { id: 3, name: "Abschnitt 3: Vergleiche & Typen", range: [21, 30] }
      ],
      2: [
        { id: 1, name: "Abschnitt 1: Conditional Statements", range: [31, 40] },
        { id: 2, name: "Abschnitt 2: Else Statements", range: [51, 60] },
        { id: 3, name: "Abschnitt 3: Elif & Logik", range: [61, 70] }
      ],
      3: [
        { id: 1, name: "Abschnitt 1: Schleifen & Zuweisung", range: [71, 80] },
        { id: 2, name: "Abschnitt 2: Schleifen Steuerung", range: [81, 90] },
        { id: 3, name: "Abschnitt 3: For Schleifen", range: [91, 100], lootboxes: [93, 100] }
      ],
      4: [
        { id: 1, name: "Abschnitt 1: Listen & Indizes", range: [101, 110], lootboxes: [103, 110] },
        { id: 2, name: "Abschnitt 2: Listen & Schleifen", range: [111, 120], lootboxes: [113, 120] },
        { id: 3, name: "Abschnitt 3: Listen Auswertung", range: [121, 130], lootboxes: [123, 130] },
        { id: 4, name: "Abschnitt 4: Listen verbinden & zählen", range: [131, 140], lootboxes: [133, 140] }
      ],
      5: [
        { id: 1, name: "Abschnitt 1: Strings & Operationen", range: [141, 150], lootboxes: [143, 150] }
      ],
      6: [
        { id: 1, name: "Abschnitt 1: Funktionen & Parameter", range: [151, 160], lootboxes: [153, 160] }
      ]
    },
    'JavaScript': {
      1: [
        { id: 1, name: "Abschnitt 1: Einführung", range: [1, 10] },
        { id: 2, name: "Abschnitt 2: Basics", range: [11, 20] },
        { id: 3, name: "Abschnitt 3: Zahlen Vergleiche", range: [21, 30] }
      ],
      2: [
        { id: 1, name: "Abschnitt 1: Logik & Typen", range: [31, 40] }
      ],
      3: [
        { id: 1, name: "Abschnitt 1: Entscheidungen", range: [41, 50] },
        { id: 2, name: "Abschnitt 2: Erweiterte Bedingungen", range: [51, 60] }
      ],
      4: [
        { id: 1, name: "Abschnitt 1: Schleifen & Zuweisung", range: [61, 70] },
        { id: 2, name: "Abschnitt 2: Fortgeschrittene Schleifen", range: [71, 80] },
        { id: 3, name: "Abschnitt 3: For Schleifen", range: [81, 90] }
      ],
      5: [
        { id: 1, name: "Abschnitt 1: Arrays", range: [91, 100] },
        { id: 2, name: "Abschnitt 2: Array Methoden", range: [101, 110] }
      ],
      6: [
        { id: 1, name: "Abschnitt 1: Funktionen & Parameter", range: [111, 120] }
      ]
    },
    'HTML': {
      1: [
        { id: 1, name: "Abschnitt 1: Einführung", range: [1, 10] },
        { id: 2, name: "Abschnitt 2: Links & Bilder", range: [11, 20] }
      ],
      2: [
        { id: 1, name: "Abschnitt 1: Formulare & Container", range: [21, 30] },
        { id: 2, name: "Abschnitt 2: Listen & Navigation", range: [31, 40] }
      ],
      3: [
        { id: 1, name: "Abschnitt 1: Semantisches HTML", range: [41, 50] },
        { id: 2, name: "Abschnitt 2: Semantische Container & Aussehen", range: [51, 60] }
      ],
      4: [
        { id: 1, name: "Abschnitt 1: Barrierefreiheit Grundlagen", range: [61, 70] }
      ],
      5: [
        { id: 1, name: "Abschnitt 1: HTML Formulare", range: [71, 80] }
      ]
    },
    'Kotlin': {
      1: [
        { id: 1, name: "Abschnitt 1: Einführung", range: [1, 10], lootboxes: [3, 10] },
        { id: 2, name: "Abschnitt 2: Variablen, Operatoren & Comments", range: [11, 20], lootboxes: [15, 20] }
      ],
      2: [
        { id: 1, name: "Abschnitt 1: Input & If Statements", range: [21, 30], lootboxes: [23, 30] },
        { id: 2, name: "Abschnitt 2: Else Statements", range: [31, 40], lootboxes: [33, 40] },
        { id: 3, name: "Abschnitt 3: Conditional Expressions", range: [41, 50], lootboxes: [43, 50] }
      ],
      3: [
        { id: 1, name: "Abschnitt 1: Schleifen & Zuweisung", range: [51, 60], lootboxes: [53, 60] }
      ],
      4: [
        { id: 1, name: "Abschnitt 1: Arrays & Listen", range: [61, 70], lootboxes: [63, 70] }
      ],
      5: [
        { id: 1, name: "Abschnitt 1: For Schleifen", range: [71, 80], lootboxes: [73, 80] },
        { id: 2, name: "Abschnitt 2: Ranges", range: [81, 90], lootboxes: [83, 90] }
      ],
      6: [
        { id: 1, name: "Abschnitt 1: Funktionen & Parameter", range: [91, 100], lootboxes: [93, 100] },
        { id: 2, name: "Abschnitt 2: Return & Lambdas", range: [101, 110], lootboxes: [103, 110] },
        { id: 3, name: "Abschnitt 3: Higher-Order Functions", range: [111, 120], lootboxes: [113, 120] }
      ],
      7: [
        { id: 1, name: "Abschnitt 1: Classes & Objects", range: [121, 130], lootboxes: [123, 130] },
        { id: 2, name: "Abschnitt 2: Getters & Setters", range: [131, 140], lootboxes: [133, 140] }
      ],
      7: [
        { id: 3, name: "Abschnitt 3: Class Functions", range: [141, 150], lootboxes: [143, 148] }
      ]

    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      setLoading(true);
      
      const unsubscribe = onSnapshot(collection(db, "users"), (snapshot) => {
        const userList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsers(userList);
        setLoading(false);
      }, (error) => {
        console.error("Error fetching users:", error);
        setLoading(false);
      });

      return () => unsubscribe();
    }
  }, [activeTab]);

  const loadLesson = async (lessonId) => {
    setSelectedLessonId(lessonId);
    setLoadingLesson(true);
    setEditingModuleIndex(null);
    const fullLessonId = `${selectedCourse}_${lessonId}`;
    try {
      const docRef = doc(db, "lessons", fullLessonId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists() && docSnap.data().content && docSnap.data().content.length > 0) {
        setModules(docSnap.data().content);
      } else {
        // Fallback auf lokale Daten oder leer
        setModules(lessonsData[fullLessonId] || []);
      }
    } catch (error) {
      console.error("Error loading lesson:", error);
      alert("Fehler beim Laden der Lektion.");
    } finally {
      setLoadingLesson(false);
    }
  };

  const saveLesson = async () => {
    if (!selectedLessonId) return;
    const fullLessonId = `${selectedCourse}_${selectedLessonId}`;
    try {
      await setDoc(doc(db, "lessons", fullLessonId), {
        content: modules
      });
      alert(`Lektion ${fullLessonId} erfolgreich gespeichert!`);
    } catch (error) {
      console.error("Error saving lesson:", error);
      alert("Fehler beim Speichern.");
    }
  };

  const updateModule = (index, field, value) => {
    const newModules = [...modules];
    newModules[index] = { ...newModules[index], [field]: value };
    setModules(newModules);
  };

  const addModule = (type) => {
    const newModule = {
      type,
      question: 'Neue Frage',
      theory: ''
    };
    
    if (type === 'info') {
      newModule.title = 'Neuer Titel';
      newModule.text = 'Hier Text eingeben...';
      newModule.buttonText = 'Weiter';
    } else if (type === 'multiple-choice') {
      newModule.options = ['Option 1', 'Option 2'];
      newModule.correctAnswer = 'Option 1';
    } else if (type === 'assembly') {
      newModule.blocks = ['print', '(', '"Hi"', ')'];
      newModule.correctOrder = ['print', '(', '"Hi"', ')'];
    } else if (type === 'bug-fixing') {
      newModule.code = 'print("Fehler)';
      newModule.options = ['print("Fehler")', 'print("Fehler")'];
      newModule.correctAnswer = 'print("Fehler")';
    }

    setModules([...modules, newModule]);
    setEditingModuleIndex(modules.length); // Sofort bearbeiten
  };

  const deleteModule = (index) => {
    if (window.confirm("Modul wirklich löschen?")) {
      const newModules = modules.filter((_, i) => i !== index);
      setModules(newModules);
      setEditingModuleIndex(null);
    }
  };

  const moveModule = (index, direction) => {
    if (direction === 'up' && index > 0) {
      const newModules = [...modules];
      [newModules[index - 1], newModules[index]] = [newModules[index], newModules[index - 1]];
      setModules(newModules);
      if (editingModuleIndex === index) setEditingModuleIndex(index - 1);
      else if (editingModuleIndex === index - 1) setEditingModuleIndex(index);
    } else if (direction === 'down' && index < modules.length - 1) {
      const newModules = [...modules];
      [newModules[index + 1], newModules[index]] = [newModules[index], newModules[index + 1]];
      setModules(newModules);
      if (editingModuleIndex === index) setEditingModuleIndex(index + 1);
      else if (editingModuleIndex === index + 1) setEditingModuleIndex(index);
    }
  };

  // Helper for input styles
  const inputStyle = {
    padding: '12px',
    borderRadius: '12px',
    border: '2px solid #e5e5e5',
    fontSize: '1rem',
    width: '100%',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    outline: 'none'
  };

  return (
    <main className="main-content" style={{ height: '100vh', overflowY: 'auto', paddingBottom: '80px' }}>
      <div className="main-content-header" style={{ position: 'sticky', top: '20px', zIndex: 10, backgroundColor: '#1cb0f6', borderBottom: '4px solid #148ac2', borderRadius: '16px', color: 'white', marginBottom: '20px', marginTop: '20px', flexShrink: 0 }}>
        <h2>Admin Dashboard</h2>
        <span>Verwaltung & Debugging Tools</span>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '30px', padding: '5px', backgroundColor: '#f0f2f5', borderRadius: '16px', flexShrink: 0 }}>
        {['users', 'lessons', 'debug'].map(tab => (
          <button
            key={tab}
            onClick={() => {
              if (navigator.vibrate) navigator.vibrate(10);
              setActiveTab(tab);
            }}
            style={{
              flex: 1,
              background: activeTab === tab ? 'white' : 'transparent',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: '700',
              fontSize: '1rem',
              color: activeTab === tab ? '#333' : '#888',
              boxShadow: activeTab === tab ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {tab === 'users' && <Users size={18} />}
            {tab === 'lessons' && <BookOpen size={18} />}
            {tab === 'debug' && <Wrench size={18} />}
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === 'users' && (
        <div style={{ backgroundColor: 'white', borderRadius: '24px', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', overflow: 'hidden', border: '1px solid #f0f0f0', flexShrink: 0 }}>
          <div style={{ padding: '25px', borderBottom: '1px solid #f0f0f0' }}>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Registrierte Nutzer <span style={{ color: '#888', fontWeight: 'normal' }}>({users.length})</span></h3>
          </div>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Lade Nutzer...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {users.map((u, i) => (
                <div key={u.id} style={{ 
                  display: 'flex', alignItems: 'center', gap: '20px', padding: '20px 25px', 
                  borderBottom: i === users.length - 1 ? 'none' : '1px solid #f8f9fa',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fcfcfc'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  {u.photoURL ? (
                    <img src={u.photoURL} alt={u.displayName} style={{ width: '48px', height: '48px', borderRadius: '14px', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                  ) : (
                    <div style={{ width: '48px', height: '48px', borderRadius: '14px', backgroundColor: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>👤</div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '700', fontSize: '1.05rem', color: '#333' }}>{u.displayName || 'Unbekannt'}</div>
                    <div style={{ fontSize: '0.9rem', color: '#888' }}>{u.email}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '20px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span style={{ fontSize: '0.8rem', color: '#aaa', fontWeight: '600' }}>GEMS</span>
                        <span style={{ fontWeight: '700', color: '#1cb0f6' }}>💎 {u.gems || 0}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span style={{ fontSize: '0.8rem', color: '#aaa', fontWeight: '600' }}>STREAK</span>
                        <span style={{ fontWeight: '700', color: '#ff9600' }}>🔥 {u.streak || 0}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'lessons' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '25px', flexShrink: 0 }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '24px', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0', display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select 
              value={selectedCourse} 
              onChange={(e) => { 
                setSelectedCourse(e.target.value); 
                setSelectedStage(1);
                setSelectedSection(1);
                setSelectedLessonId(null); 
                setModules([]); 
              }}
              style={{ ...inputStyle, width: 'auto', minWidth: '150px' }}
            >
              <option value="Python">Python</option>
              <option value="JavaScript">JavaScript</option>
              <option value="Kotlin">Kotlin</option>
              <option value="HTML">HTML</option>
            </select>

            <select 
              value={selectedStage} 
              onChange={(e) => {
                setSelectedStage(Number(e.target.value));
                setSelectedSection(1);
              }}
              style={{ ...inputStyle, width: 'auto', minWidth: '150px' }}
            >
              {Object.keys(structure[selectedCourse] || {}).map(stage => (
                <option key={stage} value={stage}>Stufe {stage}</option>
              ))}
            </select>

            <select 
              value={selectedSection} 
              onChange={(e) => setSelectedSection(Number(e.target.value))}
              style={{ ...inputStyle, width: 'auto', minWidth: '200px' }}
            >
              {structure[selectedCourse][selectedStage].map(sec => (
                <option key={sec.id} value={sec.id}>{sec.name}</option>
              ))}
            </select>

            <select 
              value={selectedLessonId || ''} 
              onChange={(e) => loadLesson(Number(e.target.value))}
              style={{ ...inputStyle, flex: 1 }}
            >
              <option value="" disabled>Lektion wählen...</option>
              {(() => {
                const section = structure[selectedCourse][selectedStage].find(s => s.id === selectedSection);
                if (!section) return null;
                const lessons = [];
                for (let i = section.range[0]; i <= section.range[1]; i++) {
                  if (section.lootboxes && section.lootboxes.includes(i)) continue;
                  lessons.push(<option key={i} value={i}>Lektion {i}</option>);
                }
                return lessons;
              })()}
            </select>
          </div>

          {selectedLessonId && (
            <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '24px', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: '1.5rem' }}>{selectedCourse} Lektion {selectedLessonId}</h3>
                    <p style={{ margin: '5px 0 0 0', color: '#888' }}>{modules.length} Module</p>
                </div>
                <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); saveLesson(); }} style={{ backgroundColor: '#58cc02', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', boxShadow: '0 4px 0 #46a302', transition: 'transform 0.1s' }}>
                  <Save size={20} /> Speichern
                </button>
              </div>

              {loadingLesson ? <p style={{ textAlign: 'center', color: '#888' }}>Lade Inhalte...</p> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {modules.map((mod, index) => (
                    <div key={index} style={{ border: '2px solid #f0f2f5', borderRadius: '16px', padding: '20px', backgroundColor: editingModuleIndex === index ? '#fff' : '#fcfcfc', transition: 'all 0.2s' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <span style={{ 
                              backgroundColor: mod.type === 'info' ? '#1cb0f6' : mod.type === 'multiple-choice' ? '#ff9600' : mod.type === 'bug-fixing' ? '#ff4b4b' : '#ce82ff', 
                              color: 'white', padding: '4px 10px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: '800', textTransform: 'uppercase' 
                          }}>
                              {mod.type}
                          </span>
                          <span style={{ fontWeight: '600', color: '#333' }}>{mod.question || mod.title || 'Unbenanntes Modul'}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); moveModule(index, 'up'); }} disabled={index === 0} style={{ padding: '8px', cursor: 'pointer', background: 'none', border: 'none', color: '#ccc' }}><ChevronUp size={20} /></button>
                          <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); moveModule(index, 'down'); }} disabled={index === modules.length - 1} style={{ padding: '8px', cursor: 'pointer', background: 'none', border: 'none', color: '#ccc' }}><ChevronDown size={20} /></button>
                          <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); setEditingModuleIndex(editingModuleIndex === index ? null : index); }} style={{ padding: '8px', cursor: 'pointer', background: editingModuleIndex === index ? '#e5f6ff' : 'none', border: 'none', borderRadius: '8px', color: '#1cb0f6' }}>
                            {editingModuleIndex === index ? <Check size={20} /> : <Edit2 size={20} />}
                          </button>
                          <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); deleteModule(index); }} style={{ padding: '8px', cursor: 'pointer', background: 'none', border: 'none', color: '#ff4b4b' }}><Trash2 size={20} /></button>
                        </div>
                      </div>

                      {editingModuleIndex === index && (
                        <div style={{ display: 'grid', gap: '15px', marginTop: '20px', paddingTop: '20px', borderTop: '2px solid #f0f2f5' }}>
                          {mod.type === 'info' && (
                            <>
                              <input type="text" placeholder="Titel" value={mod.title || ''} onChange={(e) => updateModule(index, 'title', e.target.value)} style={inputStyle} />
                              <textarea placeholder="Text" value={mod.text || ''} onChange={(e) => updateModule(index, 'text', e.target.value)} style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }} />
                              <input type="text" placeholder="Button Text" value={mod.buttonText || ''} onChange={(e) => updateModule(index, 'buttonText', e.target.value)} style={inputStyle} />
                            </>
                          )}
                          
                          {(mod.type === 'multiple-choice' || mod.type === 'assembly' || mod.type === 'bug-fixing') && (
                            <input type="text" placeholder="Frage" value={mod.question || ''} onChange={(e) => updateModule(index, 'question', e.target.value)} style={inputStyle} />
                          )}

                          {mod.type === 'multiple-choice' && (
                            <>
                              <textarea placeholder="Optionen (eine pro Zeile)" value={mod.options ? mod.options.join('\n') : ''} onChange={(e) => updateModule(index, 'options', e.target.value.split('\n'))} style={{ ...inputStyle, minHeight: '100px' }} />
                              <input type="text" placeholder="Richtige Antwort" value={mod.correctAnswer || ''} onChange={(e) => updateModule(index, 'correctAnswer', e.target.value)} style={inputStyle} />
                            </>
                          )}

                          {mod.type === 'assembly' && (
                            <>
                              <textarea placeholder="Blöcke (einer pro Zeile)" value={mod.blocks ? mod.blocks.join('\n') : ''} onChange={(e) => updateModule(index, 'blocks', e.target.value.split('\n'))} style={{ ...inputStyle, minHeight: '100px' }} />
                              <textarea placeholder="Richtige Reihenfolge (einer pro Zeile)" value={mod.correctOrder ? mod.correctOrder.join('\n') : ''} onChange={(e) => updateModule(index, 'correctOrder', e.target.value.split('\n'))} style={{ ...inputStyle, minHeight: '100px' }} />
                            </>
                          )}

                          {mod.type === 'bug-fixing' && (
                            <>
                              <textarea placeholder="Code mit Fehler" value={mod.code || ''} onChange={(e) => updateModule(index, 'code', e.target.value)} style={{ ...inputStyle, fontFamily: 'monospace', backgroundColor: '#282c34', color: '#abb2bf', border: 'none' }} />
                              <textarea placeholder="Optionen (eine pro Zeile)" value={mod.options ? mod.options.join('\n') : ''} onChange={(e) => updateModule(index, 'options', e.target.value.split('\n'))} style={{ ...inputStyle, minHeight: '100px' }} />
                              <input type="text" placeholder="Richtige Antwort" value={mod.correctAnswer || ''} onChange={(e) => updateModule(index, 'correctAnswer', e.target.value)} style={inputStyle} />
                            </>
                          )}

                          <textarea placeholder="Theorie / Erklärung (optional)" value={mod.theory || ''} onChange={(e) => updateModule(index, 'theory', e.target.value)} style={{ ...inputStyle, minHeight: '80px', backgroundColor: '#fffbe6', borderColor: '#ffe58f' }} />
                        </div>
                      )}
                    </div>
                  ))}

                  <div style={{ display: 'flex', gap: '15px', marginTop: '20px', justifyContent: 'center' }}>
                    {[
                        { type: 'info', label: 'Info', color: '#1cb0f6' },
                        { type: 'multiple-choice', label: 'Quiz', color: '#ff9600' },
                        { type: 'assembly', label: 'Puzzle', color: '#ce82ff' },
                        { type: 'bug-fixing', label: 'Bug Fix', color: '#ff4b4b' }
                    ].map(btn => (
                        <button key={btn.type} onClick={() => { if (navigator.vibrate) navigator.vibrate(10); addModule(btn.type); }} style={{ 
                            padding: '12px 20px', borderRadius: '12px', border: `2px dashed ${btn.color}`, 
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                            backgroundColor: 'transparent', color: btn.color, fontWeight: '700',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = btn.color; e.currentTarget.style.color = 'white'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = btn.color; }}
                        >
                            <Plus size={18} /> {btn.label}
                        </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'debug' && (
        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '24px', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0', flexShrink: 0 }}>
          <h3 style={{ marginTop: 0, marginBottom: '25px' }}>Debug Tools</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px' }}>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onAddHearts(); }} style={{ padding: '20px', borderRadius: '16px', border: 'none', backgroundColor: '#ff4b4b', color: 'white', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 0 #e50000', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>❤️</span>
              +5 Herzen
            </button>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onRemoveHearts(); }} style={{ padding: '20px', borderRadius: '16px', border: 'none', backgroundColor: '#ff4b4b', color: 'white', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 0 #e50000', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>❤️</span>
              -5 Herzen
            </button>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowStreak(); }} style={{ padding: '20px', borderRadius: '16px', border: 'none', backgroundColor: '#ff9600', color: 'white', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 0 #e58700', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>🔥</span>
              Streak Screen
            </button>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowSummary(); }} style={{ padding: '20px', borderRadius: '16px', border: 'none', backgroundColor: '#ffc800', color: 'white', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 0 #e5b400', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>📊</span>
              Ende Screen
            </button>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowNoHearts(); }} style={{ padding: '20px', borderRadius: '16px', border: 'none', backgroundColor: '#333', color: 'white', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 0 #000', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>💔</span>
              No Hearts
            </button>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowLootbox(); }} style={{ padding: '20px', borderRadius: '16px', border: 'none', backgroundColor: '#1cb0f6', color: 'white', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 0 #148ac2', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>🎁</span>
              Lootbox
            </button>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowInstallPWA(); }} style={{ padding: '20px', borderRadius: '16px', border: 'none', backgroundColor: '#58cc02', color: 'white', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 0 #46a302', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <Download size={24} />
              PWA Popup
            </button>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowInstallPWAIOS(); }} style={{ padding: '20px', borderRadius: '16px', border: 'none', backgroundColor: '#58cc02', color: 'white', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 0 #46a302', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <Download size={24} />
              PWA Popup (Text)
            </button>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowTheory(); }} style={{ padding: '20px', borderRadius: '16px', border: 'none', backgroundColor: '#ffc800', color: 'white', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 0 #e5b400', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>💡</span>
              Tipp Popup
            </button>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowCourseDiscovery(); }} style={{ padding: '20px', borderRadius: '16px', border: 'none', backgroundColor: '#1cb0f6', color: 'white', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 0 #148ac2', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>🌍</span>
              Kurse Popup
            </button>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowAchievement(); }} style={{ padding: '20px', borderRadius: '16px', border: 'none', backgroundColor: '#ffc800', color: 'white', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 0 #e5b400', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>🏆</span>
              Achievement
            </button>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowCertificateModal('Python'); }} style={{ padding: '20px', borderRadius: '16px', border: 'none', backgroundColor: '#1cb0f6', color: 'white', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 0 #148ac2', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>📜</span>
              Cert Popup (Py)
            </button>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onDownloadCertificate('Python'); }} style={{ padding: '20px', borderRadius: '16px', border: 'none', backgroundColor: '#333', color: 'white', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 0 #000', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <Download size={24} />
              DL Cert (Py)
            </button>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onDownloadCertificate('HTML'); }} style={{ padding: '20px', borderRadius: '16px', border: 'none', backgroundColor: '#333', color: 'white', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 0 #000', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <Download size={24} />
              DL Cert (HTML)
            </button>
            <button onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onDownloadCertificate('JavaScript'); }} style={{ padding: '20px', borderRadius: '16px', border: 'none', backgroundColor: '#333', color: 'white', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 0 #000', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <Download size={24} />
              DL Cert (JS)
            </button>
          </div>
        </div>
      )}
    </main>
  );
}

export default AdminDashboard;